import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import { TimeSeriesData, ChartDataPoint } from '../types';
import { useFormatters } from '../hooks/useVolumeData';

interface VolumeChartProps {
  data: TimeSeriesData[];
  type?: 'line' | 'bar';
  showMintBurn?: boolean;
  height?: number;
}

export const VolumeChart: React.FC<VolumeChartProps> = ({
  data,
  type = 'line',
  showMintBurn = true,
  height = 400
}) => {
  const { formatVolume } = useFormatters();

  // Transform data for chart
  const chartData: ChartDataPoint[] = data.map(item => ({
    date: item.date,
    usdcMint: parseFloat(item.usdc.mintVolume),
    usdcBurn: parseFloat(item.usdc.burnVolume),
    usdcTotal: parseFloat(item.usdc.totalVolume),
    usdtMint: parseFloat(item.usdt.mintVolume),
    usdtBurn: parseFloat(item.usdt.burnVolume),
    usdtTotal: parseFloat(item.usdt.totalVolume),
    totalMint: parseFloat(item.total.mintVolume),
    totalBurn: parseFloat(item.total.burnVolume),
    totalVolume: parseFloat(item.total.totalVolume),
    netFlow: parseFloat(item.total.netFlow)
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="glass p-4 rounded-lg border border-gray-700/50">
          <p className="text-white font-medium mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {formatVolume(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const formatXAxisLabel = (tickItem: string) => {
    // Format date labels based on the data format
    if (tickItem.includes('W')) {
      // Weekly format: 2024-W01
      return tickItem.split('-W')[1];
    } else if (tickItem.length === 7) {
      // Monthly format: 2024-01
      const [year, month] = tickItem.split('-');
      return `${month}/${year.slice(2)}`;
    } else {
      // Daily format: 2024-01-01
      const date = new Date(tickItem);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    }
  };

  if (type === 'bar') {
    return (
      <div className="chart-container">
        <h3 className="text-lg font-semibold text-white mb-4">Volume Distribution</h3>
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis 
              dataKey="date" 
              stroke="#9CA3AF"
              tickFormatter={formatXAxisLabel}
            />
            <YAxis 
              stroke="#9CA3AF"
              tickFormatter={formatVolume}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            
            {showMintBurn ? (
              <>
                <Bar dataKey="totalMint" fill="#10B981" name="Total Mint" />
                <Bar dataKey="totalBurn" fill="#EF4444" name="Total Burn" />
              </>
            ) : (
              <>
                <Bar dataKey="usdcTotal" fill="#3B82F6" name="USDC Volume" />
                <Bar dataKey="usdtTotal" fill="#8B5CF6" name="USDT Volume" />
              </>
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <h3 className="text-lg font-semibold text-white mb-4">Volume Trends</h3>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis 
            dataKey="date" 
            stroke="#9CA3AF"
            tickFormatter={formatXAxisLabel}
          />
          <YAxis 
            stroke="#9CA3AF"
            tickFormatter={formatVolume}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          
          {showMintBurn ? (
            <>
              <Line 
                type="monotone" 
                dataKey="totalMint" 
                stroke="#10B981" 
                strokeWidth={2}
                name="Total Mint"
                dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="totalBurn" 
                stroke="#EF4444" 
                strokeWidth={2}
                name="Total Burn"
                dot={{ fill: '#EF4444', strokeWidth: 2, r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="netFlow" 
                stroke="#F59E0B" 
                strokeWidth={2}
                name="Net Flow"
                dot={{ fill: '#F59E0B', strokeWidth: 2, r: 4 }}
                strokeDasharray="5 5"
              />
            </>
          ) : (
            <>
              <Line 
                type="monotone" 
                dataKey="usdcTotal" 
                stroke="#3B82F6" 
                strokeWidth={2}
                name="USDC Volume"
                dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="usdtTotal" 
                stroke="#8B5CF6" 
                strokeWidth={2}
                name="USDT Volume"
                dot={{ fill: '#8B5CF6', strokeWidth: 2, r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="totalVolume" 
                stroke="#10B981" 
                strokeWidth={3}
                name="Total Volume"
                dot={{ fill: '#10B981', strokeWidth: 2, r: 5 }}
              />
            </>
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
